<template>
  <teleport to=".stagnant-section" v-if="routes?.length > 1">
    <div class="body-1--new medium">
      <span v-for="(route, index) in routes" :key="index" class="item-breadcrumb">
        <router-link
          v-if="route.path && !route.navigate"
          class="breadcrumb-text text-decoration-none text-emerald_600"
          :to="route.path"
        >
          <v-icon icon="mdi-chevron-left" v-if="index === 0" size="22" color="emerald_600" />
          {{ route.name }}
        </router-link>
      </span>
    </div>
  </teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, ref, watch, type ComputedRef } from 'vue'
import { useRouter, type RouteRecordNormalized, type RouteRecordRaw } from 'vue-router'
import useGlobalStore from '@/stores/modules/global'
import { useNavigator } from '@/hooks'
import { TRANSPORT_JOBS_URL } from '@/utils/constants'

interface Breadcrumb {
  path: string
  name: string
  navigate: boolean
}

const navigator = useNavigator()
const globalStore = useGlobalStore()
const currentRoute = computed(() => navigator.getCurrentRoute())

const appRoutes = ref<RouteRecordNormalized[]>([])
const routerData = useRouter()

const findRouteInTree = (routes: RouteRecordRaw[], path: string) =>
  routes.find((route) => route.path === path)

const getMemoizedPaths = (() => {
  const cache = new Map<string, string[]>()
  return (url: string): string[] => {
    if (cache.has(url)) return cache.get(url)!
    if (!url.startsWith('/')) url = '/' + url
    const segments = url.split('/').filter(Boolean)
    const paths = segments.map((_, index) => '/' + segments.slice(0, index + 1).join('/'))
    cache.set(url, paths)
    return paths
  }
})()

const beautifyTitle = (title: string): string => {
  return title.endsWith(' List') ? title.replace(' List', '') : title
}

const routes: ComputedRef<Breadcrumb[]> = computed(() => {
  const actualPath = currentRoute.value.path.slice(1)
  let actualPathCollection = getMemoizedPaths(actualPath).filter(
    (path) => path !== TRANSPORT_JOBS_URL
  )
  const matchedRoutes = currentRoute.value.matched
  const lastMatchedRoute = matchedRoutes[matchedRoutes.length - 1]
  const matchedRoutePath = lastMatchedRoute?.path.slice(1) || actualPath
  // const matchedRoute = currentRoute.value.matched.at(-1)?.path.slice(1) || actualPath
  let pathCollection = getMemoizedPaths(matchedRoutePath).filter(
    (path) => path !== TRANSPORT_JOBS_URL
  )

  let breadcrumbRoutes: Breadcrumb[] = pathCollection
    .filter((path) => findRouteInTree(appRoutes.value, path))
    .map((path, index) => {
      const matchedRoute = findRouteInTree(appRoutes.value, path)
      return {
        path: actualPathCollection[index],
        name: matchedRoute ? String(matchedRoute.name) : '',
        navigate: false
      }
    })

  if (breadcrumbRoutes.length > 1) {
    const baseName = breadcrumbRoutes[0].name || 'Details'
    breadcrumbRoutes = breadcrumbRoutes.map((route) => ({
      ...route,
      name: beautifyTitle(route.name) || `${baseName} details`
    }))
  }

  return breadcrumbRoutes.length > 1
    ? [breadcrumbRoutes[0], breadcrumbRoutes[breadcrumbRoutes.length - 1]!]
    : breadcrumbRoutes
})

watch(routes, () => {
  globalStore.hasBreadcrumbs = routes.value.length > 1
})

onMounted(() => {
  nextTick(() => {
    appRoutes.value = routerData
      .getRoutes()
      .filter((route) => route && route.path && route.components)
  })
})
</script>

<style scoped lang="scss">
@use '@/styles/variables';

.app-breadcrumbs {
  position: absolute;
  top: variables.$default-app-bar-height;
  width: 100%;
  height: variables.$default-breadcrumbs-height;
  z-index: 1;
}
.item-breadcrumb {
  padding-inline: 18px 12px;

  &:first-child {
    padding-left: 0;
  }
}
.item-breadcrumb:not(:first-child) {
  position: relative;
  &::before {
    content: '/';
    position: absolute;
    width: 6px;
    left: 0;
  }
}
.item-breadcrumb:last-child {
  pointer-events: none;

  .breadcrumb-text:last-child {
    color: rgb(var(--v-theme-slate_500)) !important;
  }
}
</style>
